import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { GlassCard, StatusBadge, PageLoader, CopyButton, Spinner } from "@/components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Server, RefreshCw, ShieldCheck, Trash2, HardDrive, Cpu, Activity, Terminal, Eye, EyeOff, Play, RotateCw, Plug, Loader2, ExternalLink } from "lucide-react";

function Row({ k, v, mono }) {
  return <div className="flex justify-between gap-4 py-1.5 text-sm"><span className="text-slate-500">{k}</span><span className={`text-right text-slate-200 ${mono ? "font-mono text-xs" : ""}`}>{v}</span></div>;
}

export default function ServerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [conn, setConn] = useState(null);
  const [reveal, setReveal] = useState(false);
  const [test, setTest] = useState(null);
  const [testing, setTesting] = useState(false);
  const [expandTb, setExpandTb] = useState(10);
  const [delText, setDelText] = useState("");
  const [delTotp, setDelTotp] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/servers/${id}`).then((r) => setS(r.data)).catch(() => nav("/app/dashboard"));
  useEffect(() => { load(); }, [id]);

  useEffect(() => {
    if (s?.status === "healthy" && !conn) api.get(`/servers/${id}/connection`).then((r) => setConn(r.data)).catch(() => {});
  }, [s]);

  if (!s) return <PageLoader />;

  const act = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/servers/${id}/action/${action}`);
      toast.success(data.message); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  const runTest = async () => {
    setTesting(true); setTest(null);
    try { const { data } = await api.post(`/servers/${id}/test-connection`); setTest(data.results); }
    catch (e) { toast.error(apiErr(e)); } finally { setTesting(false); }
  };

  const rotate = async () => {
    try { const { data } = await api.post(`/servers/${id}/rotate-token`); setConn({ ...conn, token_id: data.token_id, token_secret: data.token_secret }); toast.success("Token rotated. New secret shown below."); setReveal(true); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const expand = async () => {
    try { await api.post(`/servers/${id}/expand-storage`, { storage_tb: expandTb, origin_url: window.location.origin }); toast.success(`Storage expanded to ${expandTb} TB`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const del = async () => {
    try { await api.post(`/servers/${id}/delete`, { confirm: delText, totp: delTotp || undefined }); toast.success("Server deleted"); nav("/app/dashboard"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const metrics = [
    { icon: Cpu, k: "CPU", v: `${s.cpu_pct ?? 0}%` },
    { icon: Activity, k: "Memory", v: `${s.mem_pct ?? 0}%` },
    { icon: HardDrive, k: "Storage", v: `${s.storage_used_tb ?? 0}/${s.storage_plan_tb} TB` },
    { icon: Server, k: "Uptime", v: `${s.uptime_days ?? 0} d` },
  ];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-slate-50">{s.name}</h1>
            <StatusBadge status={s.status} />
          </div>
          <div className="mt-1 font-mono text-sm text-slate-500">{s.hostname}</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <ActionBtn onClick={() => act("health-check")} busy={busy === "health-check"} icon={Activity}>Health Check</ActionBtn>
          <ActionBtn onClick={() => act("verify")} busy={busy === "verify"} icon={ShieldCheck}>Run Verification</ActionBtn>
          <ActionBtn onClick={() => act("gc")} busy={busy === "gc"} icon={RotateCw}>Garbage Collection</ActionBtn>
          <ActionBtn onClick={() => act("reboot")} busy={busy === "reboot"} icon={RefreshCw}>Reboot</ActionBtn>
        </div>
      </div>

      {s.status !== "healthy" ? (
        <GlassCard className="p-8 text-center"><Spinner className="mx-auto h-8 w-8" /><p className="mt-3 text-slate-300">This server is {s.status}. Connection details appear once it's ready.</p></GlassCard>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {metrics.map((m) => (
              <GlassCard key={m.k} className="p-4"><div className="flex items-center gap-2 text-slate-500"><m.icon className="h-4 w-4" /><span className="text-xs uppercase">{m.k}</span></div><div className="mt-2 font-display text-xl font-bold text-slate-50">{m.v}</div></GlassCard>
            ))}
          </div>

          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <GlassCard className="p-6">
              <h2 className="mb-3 font-display font-semibold text-slate-100">Server details</h2>
              <Row k="Region" v={s.region} /><Row k="PBS Version" v={s.pbs_version} mono /><Row k="Public IP" v={s.public_ip} mono />
              <Row k="Private VPN IP" v={s.private_ip} mono /><Row k="Datastore" v={s.datastore} mono />
              <Row k="Storage Used" v={`${s.storage_used_tb} TB (${s.storage_used_pct}%)`} />
              <Row k="Last Verify" v={new Date(s.last_verify_at).toLocaleString()} /><Row k="Last GC" v={new Date(s.last_gc_at).toLocaleString()} />
              <Row k="Fingerprint" v={<span className="break-all">{(s.fingerprint || "").slice(0, 30)}…</span>} mono />
            </GlassCard>

            <GlassCard className="p-6">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display font-semibold text-slate-100">Connectivity test</h2>
                <button onClick={runTest} disabled={testing} data-testid="run-connection-test" className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-400 px-3 py-1.5 text-sm font-semibold text-[#06080D] hover:bg-cyan-300 disabled:opacity-60">
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Test from Proxmox
                </button>
              </div>
              {!test && !testing && <p className="text-sm text-slate-500">Run a test to verify DNS, TCP/8007, TLS, auth, datastore, latency and throughput.</p>}
              {testing && <div className="flex items-center gap-2 text-sm text-cyan-300"><Loader2 className="h-4 w-4 animate-spin" /> Running checks…</div>}
              {test && (
                <div className="space-y-1.5" data-testid="connection-test-results">
                  {test.map((r) => (
                    <div key={r.check} className="flex items-center justify-between text-sm">
                      <span className="text-slate-400">{r.check}</span>
                      <span className={`font-mono text-xs ${r.status === "PASS" ? "text-emerald-400" : "text-cyan-300"}`}>{r.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </GlassCard>
          </div>

          {/* Connection wizard */}
          {conn && (
            <GlassCard className="mt-6 p-6">
              <div className="mb-4 flex items-center gap-2"><Terminal className="h-5 w-5 text-cyan-400" /><h2 className="font-display font-semibold text-slate-100">Connect Proxmox VE</h2></div>
              <Tabs defaultValue="web">
                <TabsList className="bg-slate-900"><TabsTrigger value="web" data-testid="conn-tab-web">Web interface</TabsTrigger><TabsTrigger value="cli" data-testid="conn-tab-cli">CLI command</TabsTrigger></TabsList>
                <TabsContent value="web" className="mt-4">
                  <p className="mb-3 text-sm text-slate-400">In Proxmox: <span className="text-slate-200">Datacenter → Storage → Add → Proxmox Backup Server</span>, then paste:</p>
                  <div className="space-y-2">
                    {[["Server", conn.hostname], ["Datastore", conn.datastore], ["Username / Token ID", conn.token_id], ["Fingerprint", conn.fingerprint]].map(([k, v]) => (
                      <Field key={k} label={k} value={v} testid={`conn-field-${k}`} />
                    ))}
                    <div className="rounded-lg border border-slate-800 bg-[#06080D] p-3">
                      <div className="mb-1 flex items-center justify-between"><span className="text-xs text-slate-500">Token Secret</span>
                        <div className="flex gap-2">
                          <button onClick={() => setReveal(!reveal)} className="text-xs text-slate-400 hover:text-cyan-300" data-testid="reveal-secret">{reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                          <CopyButton value={conn.token_secret} label="Secret" testid="copy-secret" />
                        </div>
                      </div>
                      <div className="break-all font-mono text-xs text-cyan-300">{reveal ? conn.token_secret : "•".repeat(40)}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-between rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2">
                    <span className="text-xs text-amber-200/90">Rotating credentials immediately revokes the old token.</span>
                    <button onClick={rotate} className="text-xs font-semibold text-amber-300 hover:underline" data-testid="rotate-token-btn">Rotate credentials</button>
                  </div>
                </TabsContent>
                <TabsContent value="cli" className="mt-4">
                  <div className="rounded-lg border border-slate-800 bg-slate-950 p-4">
                    <div className="mb-2 flex justify-end"><CopyButton value={conn.cli_command} label="Command" testid="copy-cli" /></div>
                    <pre className="overflow-x-auto whitespace-pre-wrap font-mono text-xs text-cyan-300">{conn.cli_command}</pre>
                  </div>
                  <p className="mt-2 text-xs text-slate-500">Secrets are never included in URLs. This command contains your one-time token secret — handle it carefully.</p>
                </TabsContent>
              </Tabs>
            </GlassCard>
          )}

          {/* Danger zone + expand */}
          <div className="mt-6 grid gap-5 lg:grid-cols-2">
            <GlassCard className="p-6">
              <h2 className="mb-2 font-display font-semibold text-slate-100">Expand storage</h2>
              <p className="mb-4 text-sm text-slate-400">Increase your plan instantly. Proration handled automatically.</p>
              <div className="mb-2 font-mono text-2xl font-bold text-cyan-300">{expandTb} TB</div>
              <Slider min={s.storage_plan_tb} max={50} step={1} value={[expandTb]} onValueChange={(v) => setExpandTb(v[0])} className="my-3" />
              <button onClick={expand} data-testid="expand-storage-btn" className="mt-2 rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-cyan-500/50">Increase Storage</button>
            </GlassCard>

            <GlassCard className="border-rose-500/20 p-6">
              <h2 className="mb-2 font-display font-semibold text-rose-300">Danger zone</h2>
              <p className="mb-4 text-sm text-slate-400">Deleting is phased and requires typed confirmation and MFA. Backups are removed after the cool-down period.</p>
              <Dialog>
                <DialogTrigger asChild><button data-testid="delete-server-btn" className="inline-flex items-center gap-2 rounded-lg border border-rose-500/40 px-4 py-2 text-sm text-rose-300 hover:bg-rose-500/10"><Trash2 className="h-4 w-4" /> Delete Backup Server</button></DialogTrigger>
                <DialogContent className="bg-[#0D111A] border-slate-800">
                  <DialogHeader><DialogTitle className="text-rose-300">Delete {s.name}?</DialogTitle></DialogHeader>
                  <p className="text-sm text-slate-400">Type the hostname <span className="font-mono text-slate-200">{s.hostname}</span> to confirm.</p>
                  <Input value={delText} onChange={(e) => setDelText(e.target.value)} placeholder="hostname" data-testid="delete-confirm-input" className="bg-slate-950 border-slate-700" />
                  <Input value={delTotp} onChange={(e) => setDelTotp(e.target.value)} placeholder="MFA code (if enabled)" data-testid="delete-totp-input" className="bg-slate-950 border-slate-700" />
                  <button onClick={del} data-testid="delete-confirm-btn" className="w-full rounded-lg bg-rose-500 py-2.5 font-semibold text-white hover:bg-rose-400">Permanently delete</button>
                </DialogContent>
              </Dialog>
            </GlassCard>
          </div>
        </>
      )}
    </div>
  );
}

function ActionBtn({ onClick, busy, icon: Icon, children }) {
  return <button onClick={onClick} disabled={busy} data-testid={`action-${String(children).toLowerCase().replace(/ /g, "-")}`} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:border-cyan-500/50 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />} {children}</button>;
}

function Field({ label, value, testid }) {
  return (
    <div className="rounded-lg border border-slate-800 bg-[#06080D] p-3">
      <div className="mb-1 flex items-center justify-between"><span className="text-xs text-slate-500">{label}</span><CopyButton value={value} label={label} testid={testid} /></div>
      <div className="break-all font-mono text-xs text-slate-200">{value}</div>
    </div>
  );
}
